'use strict';

import * as util from '../util/Common.js';
import {Point2D} from '../util/Geometry.js';
import {Bounds} from './BoxContent.js';
import * as domUtil from '../util/Dom.js';

const privateMap = util.createPrivateMapper();

const Point = Point2D;

// log2
const LOG2 = Math.log(2);

// Tile image status
const TILE_IMAGE_STATUS_NONE = 0;
const TILE_IMAGE_STATUS_LOADING = 1;
const TILE_IMAGE_STATUS_LOADED = 2;
const TILE_IMAGE_STATUS_PROCESSED = 3;
const TILE_IMAGE_STATUS_FAILURE = -1;

// const STEP_ZOOM_FACTOR = 2;
// const STEP_ROTATION_FACTOR = 16;
// const RESIZE_CHECK_INTERVAL = 1000 / 15;
const MAX_RESOURCE_LOAD_COUNT = 10;
const RESOURCE_LOAD_CHECK_INTERVAL = 1000 / 60;

const queueResourceLoad = (function(){
  const queues = [];
  let loadCount = 0;
  let sceduled = false;

  const checkQueue = function(){
    sceduled = false;
    const l = queues.length;
    for (let i = 0; i < l; i++) {
      let queue = queues[i];
      if (!queue) {
        continue;
      }

      // remove canceled
      const nq = [];
      const l1 = queue.length;
      for (let i1 = 0; i1 < l1; i1++) {
        if (queue[i1][0].canceled) {
          continue;
        }
        nq[nq.length] = queue[i1];
      }
      queues[i] = queue = nq;
      // execute load
      while (queue.length > 0 && loadCount < MAX_RESOURCE_LOAD_COUNT) {
        const q = queue.shift();
        const o = q[0];
        const v = q[1];

        // start loading
        loadCount++;
        (function(xo, xv){
          xo.state = 1;
          o.execute.call(xv, function(rv, state, errorEvent){
            loadCount--;
            if (!sceduled) {
              sceduled = true;
              setTimeout(checkQueue, RESOURCE_LOAD_CHECK_INTERVAL);
              // ns.scheduleBackground(checkQueueTask);
            }
            xo.state = state;
            switch (state) {
            case 2:
              if (xo.onload) {
                xo.onload(rv);
              }
              break;
            case -1:
              if (xo.onerror) {
                if (errorEvent && errorEvent.managed) {
                  xo.onerror(errorEvent);
                } else {
                  xo.onerror('Resource load failure. id:' + xv.resourceDef.id);
                }
              }
              break;
            case -2:
              if (xo.onabort) {
                xo.onabort();
              }
              break;
            }
          });
        })(o, v);
      }
    }
  };
  // checkQueueTask = ns.bindBackground(checkQueue);

  return function(resourceDef, execute, successCallback, failureCallback, priority){
    const o = {
      onload: function(v){
        if (typeof(successCallback) == 'function') {
          successCallback(v);
        }
      },
      onerror: function(v){
        if (typeof(failureCallback) == 'function') {
          failureCallback(v);
        }
      },
      execute: execute,
      state: 0,
      canceled: false
    };
    const v = {
      resourceDef: resourceDef
    };
    const p = Math.min(10, Math.max(0, isNaN(priority) ? 10 : (priority | 0)));

    let stateObj;
    if (!queues[p]) {
      queues[p] = [];
    }
    queues[p].push([o, v]);
    stateObj = {
      state: function(){
        return o.state;
      },
      queued: function(){
        return o.state == 0;
      },
      loading: function(){
        return o.state == 1;
      },
      loaded: function(){
        return o.state == 2;
      },
      abort: function(){
        o.canceled = true;
        o.state = -1;
      }
    };
    if (!sceduled) {
      sceduled = true;
      setTimeout(checkQueue, RESOURCE_LOAD_CHECK_INTERVAL);
      // ns.scheduleBackground(checkQueueTask);
    }
    // stateObj.appendCallbacks(successCallback, failureCallback);
    return stateObj;
  }
})();

// LevelInfo class
const LevelInfo = function(imageSize, tileSize, maxLevel, level){
  const s = 1 / (1 << (maxLevel - level));
  const sz = imageSize.mul(s).floor();
  const tn = new Point(Math.ceil(sz.x / tileSize.x), Math.ceil(sz.y / tileSize.y));

  this.level = level;
  this.scale = s;
  this.size = sz;
  this.tileNum = tn;
  this.fractionSize = new Point(sz.x - (tn.x - 1) * tileSize.x, sz.y - (tn.y - 1) * tileSize.y)
  this.pxRatio = new Point(1 / sz.x, 1 / sz.y);
};

// TiledImageInfo class
const TiledImageInfo = function(width, height, tileSize, createTileUriFunc){
  const maxLevel = Math.ceil(Math.log(Math.max(width, height)) / LOG2);
  const size = new Point(width, height);
  const levels = [];

  levels.length = maxLevel;

  for (let i = 0; i <= maxLevel; i++) {
    levels[i] = new LevelInfo(size, tileSize, maxLevel, i);
  }

  this.width = width;
  this.height = height;
  this.size = size;
  this.aspectRatio = width / height;
  this.tileSize = tileSize;
  this.minLevel = Math.min(maxLevel, Math.max(0, Math.floor(Math.log(Math.min(tileSize.x, tileSize.y)) / LOG2)));
  this.maxLevel = maxLevel;
  this.levels = levels;
  this.createTileUri = createTileUriFunc; // function(level, x, y, levelInfo)
};

// Tile class
const Tile = function(tileImage, positionX, positionY, scaleWidth, scaleHeight, opacity) {
  this.level = tileImage.level;
  this.tileImage = tileImage;
  this.positionX = positionX;
  this.positionY = positionY;
  this.scaleWidth = scaleWidth;
  this.scaleHeight = scaleHeight;
  this.opacity = opacity;
};
(function(proto){
  proto.dump = function(){
    return [
      'tileImage:{' + this.tileImage.dump() + '}',
      'positionX:' + this.positionX,
      'positionY:' + this.positionY,
      'scaleWidth:' + this.scaleWidth,
      'scaleHeight:' + this.scaleHeight,
      'opacity:' + this.opacity
    ].join(', ');
  };
})(Tile.prototype);

// TileImage class
const TileImage = function(level, x, y, levelInfo){
  this.level = level;
  this.levelInfo = levelInfo;
  this.x = x;
  this.y = y;
  this.status = TILE_IMAGE_STATUS_NONE;
  this.startRenderTime = 0;
  this.renderTarget = false;
};
(function(proto){
  // set status to processed
  proto.setProcessed = function(){
    this.status = TILE_IMAGE_STATUS_PROCESSED;
    this.startRenderTime = (new Date()).getTime();
  };
  // set status to failure
  proto.setFailure = function(){
    this.status = TILE_IMAGE_STATUS_FAILURE;
    this.startRenderTime = 0;
  };
  // unload
  proto.unload = function(){
    this.status = TILE_IMAGE_STATUS_NONE;
    this.startRenderTime = 0;
  };
  proto.dump = function(){
    return [
      'tile:' + this.level + '/' + this.x + '_' + this.y,
      'status:' + this.status,
      'renderTarget:' + this.renderTarget,
      'startRenderTime:' + this.startRenderTime
    ].join(', ');
  }
})(TileImage.prototype);

const imageBuffer = document.createElement('canvas');
const imageBufferCtx = imageBuffer.getContext('2d');
const baseImage = document.createElement('img');
domUtil.setStyles(baseImage, '', {
  margin: '0',
  padding: '0',
  border: '0',
  top: '0',
  left: '0',
  minWidth: '0',
  maxWidth: 'none',
  minHeight: '0',
  maxHeight: 'none',
  position: 'absolute'
});
const preventDefaultFunction = function(e){
  e.preventDefault();
  return false;
};

export class TiledImageResource {
  constructor(imageInfo){
    const pv = privateMap(this);
    pv.imageInfo = imageInfo;
    pv.bounds = new Bounds();
    pv.bounds.position.set(0, 0);
    pv.bounds.size.copy(pv.imageInfo.size);

    pv.noSmoothing = false;
    pv.tileBlurDuration = 150;
    pv.tileLoadCount = 0;
  }

  get imageInfo(){
    return privateMap(this).imageInfo;
  }

  get bounds(){
    return privateMap(this).bounds;
  }
  get type(){
    return 'tiledimage';
  }

  get size(){
    return privateMap(this).imageInfo.size.clone();
  }

  // get tile image
  getTileImage(level, x, y, tileImages) {
    const pv = privateMap(this);
    const _tileImages = tileImages;
    // const _tileImages = pv.tileImages;
    let lts = _tileImages[level];
    if (!lts) {
      lts = [];
      _tileImages[level] = lts;
    }
    let xts = lts[x];
    if (!xts) {
      xts = [];
      lts[x] = xts;
    }
    let t = xts[y];
    if (!t) {
      t = new TileImage(level, x, y, pv.imageInfo.levels[level]);
      xts[y] = t;
    }
    return t;
  };

  calculateTiles(viewportCenter, zoom, offset, viewport, tileCondition, renderTime) {
    const pv = privateMap(this);
    const _imageInfo = pv.imageInfo;
    const _prevTiles = tileCondition.renderTiles;
    const _hidingTiles = tileCondition.hidingTiles;
    const _tileImageCache = tileCondition.tileImageCache;
    const _tileImages = tileCondition.tileImages;

    const _log2 = LOG2;
    const _ceil = Math.ceil;
    const _round = Math.round;
    const _floor = Math.floor;
    const _log = Math.log;

    const _levelInfos = _imageInfo.levels;
    const _tileWidth = _imageInfo.tileSize.x;
    const _tileHeight = _imageInfo.tileSize.y;
    const _minLevel = _imageInfo.minLevel;
    const _maxLevel = _imageInfo.maxLevel;

    const _zoom = zoom;

    const _outlineWidth = _imageInfo.width;
    const _outlineHeight = _imageInfo.height;
    // const _vp = renderCanvas.viewport;
    // const _viewportCenter = _vp.getCenter();
    const centerX = viewportCenter.x;
    const centerY = viewportCenter.y;

    const _imageOffset = offset.add(viewportCenter);
    const _tileCacheQuota = 1;

    let tmp;
    let len;

    const _offsetX = _imageOffset.x;
    const _offsetY = _imageOffset.y;
    len = _prevTiles.length - 1;
    for (let i = len; i >= 0; i--) {
      _prevTiles[i].tileImage.renderTarget = false;
    }

    const viewportLeft = viewport.lt.x / _zoom / _outlineWidth;
    const viewportRight = viewport.rb.x / _zoom / _outlineWidth;
    const viewportTop = viewport.lt.y / _zoom / _outlineHeight;
    const viewportBottom = viewport.rb.y / _zoom / _outlineHeight;

    tmp = _ceil(_maxLevel + (_log(_zoom) / _log2));
    let startLevel = tmp > _maxLevel ? _maxLevel : (tmp < _minLevel ? _minLevel : tmp);
    let endLevel = _minLevel;
    endLevel = endLevel > startLevel ? startLevel : endLevel;

    let levelSep = 1;
    if (pv.noSmoothing) {
      endLevel = _minLevel;
      // levelSep = startLevel - endLevel;
      levelSep = Math.max(1, startLevel - endLevel);
    }
    const tileImage = this.getTileImage(_minLevel, 0, 0, _tileImages);
    if (tileImage.status < TILE_IMAGE_STATUS_LOADED) {
      startLevel = _minLevel;
    }

    // level loop
    let loadTarget;
    let processTarget;
    let levelDrawn = false;
    let calculateAgain = false;
    let renderTargetCount = 0;
    let needLoadCount = 0;
    let loadingCount = 0;
    let processingCount = 0;
    const tiles = [];
    for (let level = startLevel; level >= endLevel && !levelDrawn; level -= levelSep) {
      const levelInfo = _levelInfos[level];
      // const levelSizeX = levelInfo.size.x;
      // const levelSizeY = levelInfo.size.y;
      // const fractionSizeW = levelInfo.fractionSize.x;
      // const fractionSizeH = levelInfo.fractionSize.y;
      const tileNumX = levelInfo.tileNum.x - 1;
      const tileNumY = levelInfo.tileNum.y - 1;
      const fractionTileNumX = tileNumX;
      const fractionTileNumY = tileNumY;
      const dz = levelInfo.scale / _zoom;

      tmp = (((viewportLeft * levelInfo.size.x) / _tileWidth) | 0);
      tmp = tmp < 0 ? 0 : tmp;
      const startX = tmp;

      tmp = ((viewportRight * levelInfo.size.x) / _tileWidth) | 0;
      tmp = tmp > tileNumX ? tileNumX : tmp;
      const endX = tmp;

      tmp = (((viewportTop * levelInfo.size.y) / _tileHeight) | 0);
      tmp = tmp < 0 ? 0 : tmp;
      const startY = tmp;

      tmp = ((viewportBottom * levelInfo.size.y) / _tileHeight) | 0;
      tmp = tmp > tileNumY ? tileNumY : tmp;
      const endY = tmp;

      levelDrawn = true;
      let nx = _floor(_offsetX + _tileWidth * startX / dz);

      // x loop
      for (let x = startX; x <= endX; x++) {
        const positionX = nx;
        let scaleWidth;
        if (x == fractionTileNumX) {
          nx = _round(_offsetX + (_outlineWidth * _zoom));
          scaleWidth = nx - positionX;
        } else {
          nx = _round(_offsetX + (_tileWidth * x + _tileWidth) / dz);
          scaleWidth = nx - positionX + 0.5; // add 0.5 for rotation case
        }

        let ny = _floor(_offsetY + _tileHeight * startY / dz);
        for (let y = startY; y <= endY; y++) {
          const positionY = ny;
          let scaleHeight;
          if (y == fractionTileNumY) {
            ny = _round(_offsetY + (_outlineHeight * _zoom));
            scaleHeight = ny - positionY;
          } else {
            ny = _round(_offsetY + (_tileHeight * y + _tileHeight) / dz);
            scaleHeight = ny - positionY + 0.5; // add 0.5 for rotation case
          }

          const px = positionX;
          const py = positionY;
          const sw = scaleWidth;
          const sh = scaleHeight;

          const tileImage = this.getTileImage(level, x, y, _tileImages);
          tileImage.renderTarget = true;
          renderTargetCount++;

          let tile;
          let dx;
          let dy;
          let distance;
          switch (tileImage.status) {
            // tile image processed
            case TILE_IMAGE_STATUS_PROCESSED:
              let tileOpacity = (renderTime - tileImage.startRenderTime) / pv.tileBlurDuration;
              if (tileOpacity > 1) {
                tileOpacity = 1;
              } else {
                levelDrawn = false;
                calculateAgain = true;
              }
              tile = new Tile(tileImage, px, py, sw, sh, tileOpacity);
              tiles.push(tile);
              break;

            // tile image loaded but not yet processed
            case TILE_IMAGE_STATUS_LOADED:
              levelDrawn = false;
              processingCount++;
              dx = (centerX - (px + sw / 2));
              dy = (centerY - (py + sh / 2));
              distance = dx * dx + dy * dy;
              if (!processTarget ||
                level < processTarget.level ||
                distance < processTarget.distance) {
                processTarget = {
                  level: level,
                  distance: distance,
                  tileImage: tileImage
                };
              }
              break;

            // tile image is now loading
            case TILE_IMAGE_STATUS_LOADING:
              // calculateAgain = true;
              levelDrawn = false;
              loadingCount++;
              break;

            // new tile image
            case TILE_IMAGE_STATUS_NONE:
              levelDrawn = false;
              needLoadCount++;
              dx = (centerX - (px + sw / 2));
              dy = (centerY - (py + sh / 2));
              distance = dx * dx + dy * dy;
              if (!loadTarget ||
                level < loadTarget.level ||
                distance < loadTarget.distance) {
                loadTarget = {
                  level: level,
                  forceLoad: level == _minLevel,
                  distance: distance,
                  tileImage: tileImage
                };
              }
              break;

            // filed tile image
            case TILE_IMAGE_STATUS_FAILURE:
              break;
          }
        }
        // end of y loop
      }
      // end of x loop
    }
    // end of level loop

    if (loadTarget) {
      _tileImageCache.push(loadTarget.tileImage);
      calculateAgain = true;
    }

    // unload extra cache
    const nextCache = [];
    const unloadCandidates = [];
    len = _tileImageCache.length;
    for (let i = 0; i < len; i++) {
      const tileImage = _tileImageCache[i];
      if (tileImage.level <= _minLevel || tileImage.renderTarget) {
        nextCache.push(tileImage);
      } else {
        unloadCandidates.push(tileImage);
      }
    }
    while (nextCache.length < _tileCacheQuota && unloadCandidates.length > 0) {
      nextCache.push(unloadCandidates.pop());
    }
    len = unloadCandidates.length;
    for (let i = 0; i < len; i++) {
      unloadCandidates[i].unload();
    }
    tileCondition.tileImageCache = nextCache;

    _hidingTiles.length = 0;
    for (let i = _prevTiles.length - 1; i >= 0; i--) {
      const tileImage = _prevTiles[i].tileImage;
      if (!tileImage) {
        continue;
      }
      if (!tileImage.renderTarget && tileImage.status == TILE_IMAGE_STATUS_PROCESSED) {
        _hidingTiles.push(tileImage);
      }
    }

    this.progressInfo = {
      renderTargetCount: renderTargetCount,
      needLoadCount: needLoadCount,
      loadingCount: loadingCount,
      processingCount: processingCount
    };
    tileCondition.renderTiles = tiles;
    return {
      loadTarget: loadTarget,
      renderTiles: tiles,
      calculateAgain: calculateAgain
    };
  };
}

export class TileCondition {
  constructor(resource){
    this.startTime = (new Date()).getTime()
    this.resource = resource;
    this.imageInfo = resource.imageInfo;
    this.tileLoadCount = 0;
    this.renderTiles = [];
    this.hidingTiles = [];
    this.tileImages = [];
    this.tileImageCache = [];
    this.destroyed = false;
  }

  onTileImageLoad(tileImage, success, data) {
    this.tileLoadCount--;
    tileImage.status = TILE_IMAGE_STATUS_LOADED;
    if (success) {
      tileImage.setProcessed();
      tileImage.image = data;
    } else {
      tileImage.setFailure();
    }
  };

  destroy(){
    this.destroyed = true;
  }

  loadTile(imageInfo, tileImage, forceLoad, requestRender){
    const pv = privateMap(this);
    if (this.destroyed) {
      return;
    }
    if (!forceLoad && this.tileLoadCount > 3) {
      // tiles loading exceeds limit
      return;
    }
    this.tileLoadCount++;

    tileImage.status = TILE_IMAGE_STATUS_LOADING;
    const imageUrl = imageInfo.createTileUri(tileImage.level, tileImage.x, tileImage.y, tileImage.levelInfo);

    const _self = this;
    queueResourceLoad(imageUrl, function(finishFunc){
      let img = baseImage.cloneNode(true);
      img.ondragstart = preventDefaultFunction;
      img.onload = function(){
        img.onload = null;
        img.onerror = null;
        img.onabort = null;
        finishFunc(img, 2);
        img = null;
      };
      img.onerror = function(e){
        img.onload = null;
        img.onerror = null;
        img.onabort = null;
        img = null;
        finishFunc(null, -1, e);
      };
      img.onabort = function(){
        img.onload = null;
        img.onerror = null;
        img.onabort = null;
        img = null;
        finishFunc(null, -2);
      };
      img.src = imageUrl;
    }, function(v){
      if (_self.destroyed) {
        return;
      }
      _self.onTileImageLoad(tileImage, true, v);
      requestRender();
    }, function(){
      if (_self.destroyed) {
        return;
      }
      _self.onTileImageLoad(tileImage, false);
    }, 0);
  };
}

export class TiledImageRenderer {
  constructor(options){
    options = options ? options : {}
    this.imageBackgroundColor = options.imageBackgroundColor ? options.imageBackgroundColor : '#333'
  }

  render(view, resource, control, session){
    const ctx = view.context2d;
    const disposition = control.box.disposition;

    const bctx = imageBufferCtx;
    const angle = disposition.angle;
    let zoom = disposition.zoom;
    const size = resource.size;
    const bounds = resource.bounds;

    if (!session.tileCondition) {
      session.tileCondition = new TileCondition(view, resource);
      session.eventManager.addEventListener('destroy', function(){
        session.tileCondition.destroy();
      })
    }

    const scale = new Point2D()
    scale.x = bounds.size.x / size.x
    scale.y = bounds.size.y / size.y
    const tileZoom = zoom * (Math.min(scale.x, scale.y) + Math.abs(scale.x - scale.y) * 0.5)
    const relZoom = zoom / tileZoom
    scale.x *= relZoom
    scale.y *= relZoom

    const vc = view.center;
    const da = vc.angle();
    const dt = new Point(
      Math.max(Math.abs(Math.cos(da - angle)), Math.abs(Math.cos(-da - angle))),
      Math.max(Math.abs(Math.sin(da - angle)), Math.abs(Math.sin(-da - angle)))
    ).mul(vc.distance());
    dt.x /= scale.x
    dt.y /= scale.y

    const sz = size.mul(tileZoom)
    const targetOfs = bounds.position.mul(zoom)
    const scaleOfs = disposition.offset.add(targetOfs)
    scaleOfs.x /= scale.x;
    scaleOfs.y /= scale.y;

    const ofs = scaleOfs.sub(sz.div(2));
    const cp = ofs.negate();
    const viewport = {
      lt: cp.sub(dt),
      rb: cp.add(dt)
    };

    const calcResult = resource.calculateTiles(vc, tileZoom, ofs, viewport, session.tileCondition, (new Date()).getTime());
    if (calcResult.loadTarget) {
      session.tileCondition.loadTile(resource.imageInfo, calcResult.loadTarget.tileImage, calcResult.loadTarget.forceLoad, function(){
        view.requestRender();
      });
    }

    if (calcResult.calculateAgain || session.tileCondition.tileLoadCount > 0) {
      view.requestRender();
    }

    const targetTiles = calcResult.renderTiles;
    ctx.save();

    const rotateCenter = view.center;
    ctx.translate(rotateCenter.x, rotateCenter.y);
    ctx.rotate(angle);
    ctx.scale(scale.x, scale.y)
    ctx.translate(-rotateCenter.x, -rotateCenter.y);

    if (ctx.globalAlpha < 1) {
      // use image buffer for alpha blending
      const vsz = dt.mul(2);
      imageBuffer.width = vsz.x;
      imageBuffer.height = vsz.y;

      const rm = dt.sub(vc);
      for (let i = targetTiles.length - 1; i >= 0; i--) {
        const tile = targetTiles[i];
        const tileImage = tile.tileImage;
        const img = tileImage.image;

        bctx.globalAlpha = tile.opacity;
        bctx.drawImage(img, rm.x + tile.positionX, rm.y + tile.positionY, tile.scaleWidth, tile.scaleHeight);
      }
      ctx.drawImage(imageBuffer, vc.x - vsz.x / 2, vc.y - vsz.y / 2, vsz.x, vsz.y);
    } else {
      // draw image directly
      for (let i = targetTiles.length - 1; i >= 0; i--) {
        const tile = targetTiles[i];
        const tileImage = tile.tileImage;
        const img = tileImage.image;
        ctx.globalAlpha = tile.opacity;
        ctx.drawImage(img, tile.positionX, tile.positionY, tile.scaleWidth, tile.scaleHeight);

        //----------------------------
        // debug output tile box
        // ctx.globalAlpha = 1;
        // ctx.lineWidth = 2;
        // ctx.strokeStyle = '#f00';
        // strokeBox(ctx, tile.positionX, tile.positionY, tile.scaleWidth, tile.scaleHeight)
        //----------------------------
      }
    }

    //----------------------------
    // debug output viewport box
    // ctx.globalAlpha = 1;
    // ctx.lineWidth = 2;
    // ctx.strokeStyle = '#0f0';
    // strokeBox(ctx, vc.x + viewport.lt.x - (sz.x / 2) + scaleOfs.x, vc.y + viewport.lt.y - (sz.y / 2) + scaleOfs.y, dt.x * 2, dt.y * 2)
    //----------------------------

    //----------------------------
    // debug output iamge box
    // ctx.strokeStyle = '#0f0'
    // ctx.strokeRect(ofs.x + vc.x + 0.5, ofs.y + vc.y + 0.5, sz.x - 1, sz.y - 1);
    //----------------------------

    ctx.restore();
  };
}

function strokeBox(ctx, x, y, w, h) {
  ctx.strokeRect(x, y, w, h);
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x + w, y + h);
  ctx.moveTo(x + w, y);
  ctx.lineTo(x, y + h);
  ctx.closePath()
  ctx.stroke()
}

export {TiledImageInfo};
